import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Paperclip, User } from "lucide-react";
import { Skeleton } from "../../ui/skeleton";

import type { Email, EmailRecipient } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardSubtitle,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Email, EmailRecipient };

type EmailCardProps = Email & BaseEntityCardProps;

const decodeHtmlEntities = (text: string) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

export function EmailCardAvatar({ className, style }: Partial<EmailCardProps>) {
  return (
    <Avatar className={cn("h-10 w-10 shrink-0", className)} style={style}>
      <AvatarFallback>
        <User className="h-5 w-5 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
}

export function EmailCard({
  platform,
  subject,
  body_preview,
  sender_name,
  sender_address = "",
  has_attachments,
  timestamp,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: EmailCardProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <EmailCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            {sender_name || sender_address || "Me"}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {timestamp &&
                formatDistanceToNow(new Date(timestamp), {
                  addSuffix: true,
                })}
            </span>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardSubtitle className="flex items-center gap-1.5">
            <span className="font-medium line-clamp-1">{subject}</span>
            {has_attachments && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Has attachments</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </BaseEntityCardSubtitle>
          <BaseEntityCardDescription className="line-clamp-1">
            {body_preview && decodeHtmlEntities(body_preview)}
          </BaseEntityCardDescription>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function EmailCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[90px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-4 w-3/4 mb-1.5" />
        <Skeleton className="h-3 w-1/2" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
